import { Effect, Option, Result, Schema as S } from "effect";
import * as Command from "foldkit/command";
import { evo } from "foldkit/struct";
import * as Update from "foldkit/update";

import {
	adjustDeveloperCredits,
	deleteBillingData,
	deleteGeneratedData,
} from "../../api/commands/developer";
import { toMessage } from "../../api/errors";
import type { Direction } from "../../api/schemas/ledger";
import {
	readDeveloperEnabled,
	readTagInputVariant,
	writeDeveloperEnabled,
	writeTagInputVariant,
} from "../../app/developerOptions";
import { Dialog } from "../../widgets/dialog";
import { TagInputVariant } from "../developers/model";
import type { Page, PageReturn } from "../shared";
import {
	BATCH_SIZE,
	GenerationConfig,
	type Plan,
	Step,
	buildPlan,
	defaultConfig,
	runBatch,
} from "./generator";
import { Message, OutMessage } from "./message";
import { type Generation, type Model, isRunning } from "./model";

// COMMANDS

export const ReadOptions = Command.define("ReadDeveloperOptions", {
	messages: [Message.CompletedReadOptions],
	execute: Effect.sync(() =>
		Message.CompletedReadOptions({
			enabled: readDeveloperEnabled(),
			variant: readTagInputVariant(),
		}),
	),
});

export const PersistEnabled = Command.define("PersistDeveloperEnabled", {
	args: { enabled: S.Boolean },
	messages: [Message.CompletedPersist],
	execute: ({ enabled }) =>
		Effect.sync(() => writeDeveloperEnabled(enabled)).pipe(
			Effect.as(Message.CompletedPersist()),
		),
});

export const PersistVariant = Command.define("PersistDeveloperTagVariant", {
	args: { variant: TagInputVariant },
	messages: [Message.CompletedPersist],
	execute: ({ variant }) =>
		Effect.sync(() => writeTagInputVariant(variant)).pipe(
			Effect.as(Message.CompletedPersist()),
		),
});

export const AdjustCredits = adjustDeveloperCredits.command(
	Message.CompletedAdjust,
	({ generation, result }) => Message.CompletedAdjust({ generation, result }),
);
export const DeleteBilling = deleteBillingData.command(
	Message.CompletedDeleteBilling,
);
export const Cleanup = deleteGeneratedData.command(Message.CompletedCleanup);

/** Random content is I/O for the Model's purposes: the plan arrives as a Message. */
export const BuildPlan = Command.define("BuildGenerationPlan", {
	args: { config: GenerationConfig },
	messages: [Message.CompletedBuildPlan],
	execute: ({ config }) =>
		buildPlan(config).pipe(
			Effect.map((plan) => Message.CompletedBuildPlan({ plan })),
		),
});

export const RunBatch = Command.define("RunGenerationBatch", {
	args: {
		steps: S.Array(Step),
		index: S.Number,
		last: S.Boolean,
		generation: S.Number,
	},
	messages: [Message.CompletedBatch],
	execute: ({ steps, index, last, generation }) =>
		runBatch(steps, last).pipe(
			Effect.as(Message.CompletedBatch({ index, generation })),
		),
});

// UPDATE

type Return = PageReturn<Model, Message, OutMessage>;

const dialogChild = {
	read: (model: Model) => Option.some(model.dialog),
	write: (model: Model, dialog: Dialog.Model) =>
		evo(model, { dialog: () => dialog }),
	toParentMessage: (message: Dialog.Message) =>
		Message.GotDialogMessage({ message }),
	foldOutMessage: () => (model: Model) => ({ model }),
};
const foldDialog = Update.foldChild({
	update: (dialog: Dialog.Model, message: Dialog.Message) =>
		Dialog.update(dialog, message),
	...dialogChild,
});
const openDialog = Update.foldChildStep({
	update: Dialog.open,
	...dialogChild,
});
const closeDialog = Update.foldChildStep({
	update: Dialog.close,
	...dialogChild,
});

const askConfirm = (model: Model, action: Model["pendingConfirm"]): Return =>
	openDialog(evo(model, { pendingConfirm: () => action }));

const phaseOf = (plan: Plan, index: number): Generation["phase"] => {
	const step = plan.steps[index * BATCH_SIZE];
	if (step === undefined) return "done";
	return (
		{
			"create-record": "creating-records",
			"edit-record": "editing-records",
			"create-entry": "creating-entries",
			"edit-entry": "editing-entries",
		} as const
	)[step.phase];
};

const batchCommand = (plan: Plan, index: number, generation: number) => {
	const start = index * BATCH_SIZE;
	return RunBatch({
		steps: plan.steps.slice(start, start + BATCH_SIZE),
		index,
		last: start + BATCH_SIZE >= plan.steps.length,
		generation,
	});
};

const withGeneration = (
	model: Model,
	f: (generation: Generation) => Generation,
): Model => evo(model, { generation: f });

const adjust = (
	model: Model,
	direction: typeof Direction.Type,
	generation: number,
): Return => {
	const amount = model.amount.trim();
	const notes = model.notes.trim();
	return {
		model: evo(model, {
			submitting: () => Option.some(direction),
			creditError: () => Option.none(),
		}),
		commands: [
			AdjustCredits({
				amount,
				direction,
				...(notes ? { notes } : {}),
				generation,
			}),
		],
	};
};

const confirmed = (model: Model, generation: number): Return => {
	const closed = closeDialog(
		evo(model, { pendingConfirm: () => Option.none() }),
	);
	const then = (next: (m: Model) => Return): Return => {
		const result = next(closed.model);
		return {
			model: result.model,
			commands: [...(closed.commands ?? []), ...(result.commands ?? [])],
		};
	};
	return Option.match(model.pendingConfirm, {
		onNone: () => closed,
		onSome: (action) => {
			switch (action) {
				case "credit":
				case "debit":
					return then((m) => adjust(m, action, generation));
				case "billing":
					return then((m) => ({
						model: evo(m, {
							billingPending: () => true,
							billingFeedback: () => Option.none(),
						}),
						commands: [DeleteBilling({ generation })],
					}));
				case "cleanup":
					return then((m) => ({
						model: withGeneration(m, (g) =>
							evo(g, {
								cleaning: () => true,
								error: () => Option.none(),
								success: () => Option.none(),
							}),
						),
						commands: [Cleanup({ generation })],
					}));
			}
		},
	});
};

export const initial: Model = {
	page: "Developer",
	developerEnabled: false,
	tagInputVariant: "external",
	amount: "10",
	notes: "",
	submitting: Option.none(),
	lastAdjustment: Option.none(),
	creditError: Option.none(),
	billingPending: false,
	billingFeedback: Option.none(),
	dialog: Dialog.init({ id: "confirm_developer_dialog" }),
	pendingConfirm: Option.none(),
	generation: {
		config: defaultConfig,
		plan: Option.none(),
		phase: "idle",
		progress: 0,
		cancelRequested: false,
		error: Option.none(),
		success: Option.none(),
		cleaning: false,
	},
};

export const page: Page<Model, Message, OutMessage> = {
	init: () => ({ model: initial, commands: [ReadOptions()] }),
	changedRoute: (model) => ({ model }),
	update: (model, message, shared) =>
		Message.match<Return>(message, {
			CompletedReadOptions: ({ enabled, variant }) => ({
				model: evo(model, {
					developerEnabled: () => enabled,
					tagInputVariant: () => variant,
				}),
			}),
			ClickedDeveloperOptions: () => ({
				model: evo(model, { developerEnabled: (enabled) => !enabled }),
				commands: [PersistEnabled({ enabled: !model.developerEnabled })],
			}),
			SelectedTagVariant: ({ variant }) => ({
				model: evo(model, { tagInputVariant: () => variant }),
				commands: [PersistVariant({ variant })],
			}),
			ToggledIntegrations: ({ enabled }) => ({
				model,
				outMessage: OutMessage.IntegrationsToggled({ enabled }),
			}),
			CompletedPersist: () => ({ model }),
			ChangedAmount: ({ value }) => ({
				model: evo(model, { amount: () => value }),
			}),
			ChangedNotes: ({ value }) => ({
				model: evo(model, { notes: () => value }),
			}),
			ClickedAdjust: ({ direction }) =>
				model.amount.trim() === ""
					? {
							model: evo(model, {
								creditError: () => Option.some("Enter an amount to adjust."),
							}),
						}
					: askConfirm(model, Option.some(direction)),
			ClickedDeleteBilling: () =>
				askConfirm(
					evo(model, { billingFeedback: () => Option.none() }),
					Option.some("billing"),
				),
			ClickedCleanup: () => askConfirm(model, Option.some("cleanup")),
			ClickedConfirm: () => confirmed(model, shared.generation),
			GotDialogMessage: ({ message }) => foldDialog(model, message),
			CompletedAdjust: ({ generation, result }) => ({
				model:
					generation !== shared.generation
						? model
						: Result.match(result, {
								onSuccess: (adjustment) =>
									evo(model, {
										submitting: () => Option.none(),
										lastAdjustment: () => Option.some(adjustment),
										creditError: () => Option.none(),
									}),
								onFailure: (error) =>
									evo(model, {
										submitting: () => Option.none(),
										creditError: () => Option.some(toMessage(error)),
									}),
							}),
			}),
			CompletedDeleteBilling: ({ generation, result }) => ({
				model:
					generation !== shared.generation
						? model
						: evo(model, {
								billingPending: () => false,
								billingFeedback: () =>
									Option.some(
										Result.match(result, {
											onSuccess: () => ({
												kind: "success" as const,
												message:
													"Billing data deleted. Ledger accounts will be recreated on the next billing action.",
											}),
											onFailure: (error) => ({
												kind: "error" as const,
												message: toMessage(error),
											}),
										}),
									),
							}),
			}),
			CompletedCleanup: ({ generation, result }) => ({
				model:
					generation !== shared.generation
						? model
						: withGeneration(model, (g) =>
								Result.match(result, {
									onSuccess: ({ deletedEvents }) =>
										evo(g, {
											cleaning: () => false,
											success: () =>
												Option.some(
													`Cleaned up ${deletedEvents} generated events`,
												),
										}),
									onFailure: (error) =>
										evo(g, {
											cleaning: () => false,
											error: () => Option.some(toMessage(error)),
										}),
								}),
							),
			}),
			ChangedGenerationConfig: ({ key, value }) => ({
				model: withGeneration(model, (g) =>
					evo(g, { config: (config) => ({ ...config, [key]: value }) }),
				),
			}),
			ClickedGenerate: () =>
				isRunning(model.generation)
					? { model }
					: {
							model: withGeneration(model, (g) =>
								evo(g, {
									error: () => Option.none(),
									success: () => Option.none(),
									progress: () => 0,
									cancelRequested: () => false,
									plan: () => Option.none(),
								}),
							),
							commands: [BuildPlan({ config: model.generation.config })],
						},
			CompletedBuildPlan: ({ plan }) =>
				plan.steps.length === 0
					? {
							model: withGeneration(model, (g) =>
								evo(g, {
									phase: () => "done",
									success: () => Option.some(summary(plan)),
								}),
							),
						}
					: {
							model: withGeneration(model, (g) =>
								evo(g, {
									plan: () => Option.some(plan),
									phase: () => phaseOf(plan, 0),
								}),
							),
							commands: [batchCommand(plan, 0, shared.generation)],
						},
			ClickedCancelGeneration: () => ({
				model: withGeneration(model, (g) =>
					evo(g, { cancelRequested: () => true }),
				),
			}),
			CompletedBatch: ({ index, generation }) => {
				const g = model.generation;
				if (generation !== shared.generation || Option.isNone(g.plan)) {
					return { model };
				}
				const plan = g.plan.value;
				const progress = Math.min(plan.steps.length, (index + 1) * BATCH_SIZE);
				if (g.cancelRequested) {
					return {
						model: withGeneration(model, (gen) =>
							evo(gen, {
								phase: () => "idle",
								progress: () => progress,
								plan: () => Option.none(),
								success: () =>
									Option.some(
										`Cancelled after ${progress}/${plan.steps.length} steps`,
									),
							}),
						),
					};
				}
				if (progress >= plan.steps.length) {
					return {
						model: withGeneration(model, (gen) =>
							evo(gen, {
								phase: () => "done",
								progress: () => progress,
								plan: () => Option.none(),
								success: () => Option.some(summary(plan)),
							}),
						),
					};
				}
				return {
					model: withGeneration(model, (gen) =>
						evo(gen, {
							progress: () => progress,
							phase: () => phaseOf(plan, index + 1),
						}),
					),
					commands: [batchCommand(plan, index + 1, shared.generation)],
				};
			},
		}),
};

const summary = ({ summary }: Plan): string =>
	`Generated ${summary.records} records, ${summary.recordEdits} record edits, ${summary.entries} entries, ${summary.entryEdits} entry edits`;
